import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardContent, CardHeader, CardTitle, Separator } from '@ah/design';
import { BookOpen, Linkedin, Quote, UserPlus, Sparkles, ArrowLeft, Image as ImageIcon } from 'lucide-react';
import { useGql } from '@ah/design';
import { POST_GATHERING_QUERY, LINKEDIN_PREVIEW_QUERY, PUBLISH_LINKEDIN_MUTATION, ADD_CONNECTION_MUTATION, GENERATE_RECAP_MUTATION, type Roommate, type Recap, type LinkedInMentionable, type SharePreview } from '../shared/index.ts';

export function PostGathering() {
  const gql = useGql();
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['recap', id],
    queryFn: () =>
      gql.request<{
        gathering: { id: string; title: string } | null;
        roommates: Roommate[];
        recapFor: Recap | null;
      }>(POST_GATHERING_QUERY, { gatheringId: id }),
  });

  const addConn = useMutation({
    mutationFn: (otherUserId: string) => gql.request<unknown>(ADD_CONNECTION_MUTATION, { gatheringId: id, otherUserId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recap', id] }),
  });

  const genRecap = useMutation({
    mutationFn: () => gql.request<unknown>(GENERATE_RECAP_MUTATION, { gatheringId: id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recap', id] }),
  });

  // Kick off generation once, when we've loaded and there is no recap yet.
  const triedGenerate = useRef(false);
  useEffect(() => {
    if (!data || triedGenerate.current) return;
    if (!data.recapFor) {
      triedGenerate.current = true;
      genRecap.mutate();
    }
  }, [data, genRecap]);

  if (isLoading || !data) return <p className="p-10 text-muted-foreground">Loading recap…</p>;
  const { gathering, roommates, recapFor } = data;

  return (
    <div className="max-w-3xl mx-auto px-6 py-10 space-y-8">
      <Link to="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
        <ArrowLeft size={14} /> Home
      </Link>
      <header>
        <h1 className="text-4xl font-bold">{gathering?.title ?? 'Recap'}</h1>
        <p className="text-muted-foreground text-sm mt-1">Everything from your Table, ready to keep.</p>
      </header>

      {/* Roommates — add as connections */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><UserPlus size={16} /> People at your Table</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {roommates.length === 0 ? (
            <p className="text-sm text-muted-foreground">No one else was in your breakout.</p>
          ) : (
            roommates.map((r) => (
              <div key={r.id} className="flex items-center gap-3">
                {r.pictureUrl ? (
                  <img src={r.pictureUrl} alt={r.name} className="size-10 rounded-full object-cover" />
                ) : (
                  <div className="size-10 rounded-full bg-muted grid place-items-center text-sm">{initials(r.name)}</div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{r.name}</p>
                  {r.linkedinUrl ? (
                    <a href={r.linkedinUrl} target="_blank" rel="noopener" className="text-xs text-muted-foreground hover:underline inline-flex items-center gap-1">
                      <Linkedin size={12} /> LinkedIn
                    </a>
                  ) : null}
                </div>
                {r.alreadyConnected ? (
                  <span className="text-xs text-muted-foreground">Connected ✓</span>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => addConn.mutate(r.id)} disabled={addConn.isPending}>Add</Button>
                )}
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {recapFor ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Sparkles size={16} /> AI recap</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{recapFor.summary}</p>
              <Separator />
              <section>
                <h3 className="font-medium text-sm mb-2">Key takeaways</h3>
                <ul className="list-disc pl-5 text-sm space-y-1">
                  {recapFor.keyTakeaways.map((k, i) => <li key={i}>{k}</li>)}
                </ul>
              </section>
              {recapFor.notableQuotes.length > 0 ? (
                <section>
                  <h3 className="font-medium text-sm mb-2">Quotes worth remembering</h3>
                  {recapFor.notableQuotes.map((q, i) => (
                    <blockquote key={i} className="text-sm italic border-l-2 border-muted pl-3 my-2">
                      <Quote size={12} className="inline mr-1 text-muted-foreground" />
                      {q.quote}
                      <span className="not-italic text-xs text-muted-foreground"> — {q.speaker}</span>
                    </blockquote>
                  ))}
                </section>
              ) : null}
              {recapFor.booksMentioned.length > 0 ? (
                <section>
                  <h3 className="font-medium text-sm mb-2 flex items-center gap-1"><BookOpen size={14} /> Books mentioned</h3>
                  <ul className="list-disc pl-5 text-sm space-y-1">
                    {recapFor.booksMentioned.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                </section>
              ) : null}
              {recapFor.conceptsMentioned.length > 0 ? (
                <section>
                  <h3 className="font-medium text-sm mb-2">Concepts & ideas</h3>
                  <p className="text-sm text-muted-foreground">{recapFor.conceptsMentioned.join(' · ')}</p>
                </section>
              ) : null}
            </CardContent>
          </Card>

          <LinkedInShareCard gatheringId={id} fallbackDraft={recapFor.linkedinDraft} />
        </>
      ) : (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground text-sm">
            {genRecap.isPending
              ? 'Writing your recap… this usually takes under a minute.'
              : genRecap.isError
                ? `Recap generation failed: ${(genRecap.error as Error).message}`
                : 'Your recap is being prepared.'}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function initials(n: string) {
  const p = n.trim().split(/\s+/);
  return ((p[0]?.[0] ?? '') + (p[1]?.[0] ?? '')).toUpperCase() || '?';
}

// ──────────────────────────────────────────────────────────────────
// LinkedIn share composer
// ──────────────────────────────────────────────────────────────────

function LinkedInShareCard({ gatheringId, fallbackDraft }: { gatheringId: string; fallbackDraft: string }) {
  const gql = useGql();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['linkedinPreview', gatheringId],
    queryFn: () =>
      gql.request<{ linkedinSharePreview: SharePreview | null }>(LINKEDIN_PREVIEW_QUERY, { gatheringId }),
    staleTime: 60_000,
  });

  const preview = data?.linkedinSharePreview ?? null;
  const [commentary, setCommentary] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const [publishedUrn, setPublishedUrn] = useState<string | null>(null);

  // Seed editable state once the preview loads.
  useEffect(() => {
    if (preview && commentary === null) {
      setCommentary(preview.suggestedText || fallbackDraft);
    }
    if (preview && selected === null) {
      setSelected(new Set(preview.mentionables.map((m) => m.userId)));
    }
  }, [preview, commentary, selected, fallbackDraft]);

  const publish = useMutation({
    mutationFn: (args: { commentary: string; mentionedUserIds: string[] }) =>
      gql.request<{ publishLinkedInShare: { postUrn: string; feedUrl: string } }>(PUBLISH_LINKEDIN_MUTATION, {
        gatheringId,
        ...args,
      }),
    onSuccess: (r) => setPublishedUrn(r.publishLinkedInShare.postUrn),
  });

  const mentionables = useMemo(() => preview?.mentionables ?? [], [preview]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Linkedin size={16} /> Share on LinkedIn</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground py-8 text-center">
          Building your composite photo…
        </CardContent>
      </Card>
    );
  }

  if (isError || !preview) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Linkedin size={16} /> Share on LinkedIn</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          We couldn't prepare the share preview
          {isError ? `: ${(error as Error).message}` : '.'}
        </CardContent>
      </Card>
    );
  }

  if (publishedUrn) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Linkedin size={16} /> Posted to LinkedIn ✓</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p>Your post is live. Participants were tagged, along with @Atlas House.</p>
          <a
            href="https://www.linkedin.com/feed/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 underline"
          >
            <Linkedin size={14} /> Open LinkedIn feed
          </a>
        </CardContent>
      </Card>
    );
  }

  const toggleMention = (userId: string) => {
    setSelected((prev) => {
      const next = new Set(prev ?? []);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const canPublish = preview.hasLinkedInToken && (commentary ?? '').trim().length > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Linkedin size={16} /> Share on LinkedIn</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded overflow-hidden border">
          <img src={preview.imageUrl} alt="Composite of your Table" className="w-full block" />
          <p className="text-[11px] text-muted-foreground px-2 py-1 inline-flex items-center gap-1">
            <ImageIcon size={11} /> Auto-generated from everyone's gathering snapshots.
          </p>
        </div>

        <textarea
          className="w-full min-h-36 border rounded p-3 text-sm bg-background"
          value={commentary ?? ''}
          onChange={(e) => setCommentary(e.target.value)}
        />

        {mentionables.length > 0 ? (
          <section>
            <p className="text-xs text-muted-foreground mb-2">Tag Table-mates (uncheck to skip):</p>
            <div className="flex flex-wrap gap-2">
              {mentionables.map((m) => {
                const on = selected?.has(m.userId) ?? false;
                return (
                  <button
                    key={m.userId}
                    type="button"
                    onClick={() => toggleMention(m.userId)}
                    className={
                      'text-xs rounded-full border px-3 py-1 transition ' +
                      (on
                        ? 'bg-foreground text-background border-foreground'
                        : 'bg-background text-muted-foreground hover:text-foreground')
                    }
                  >
                    @{m.name}
                  </button>
                );
              })}
            </div>
          </section>
        ) : null}

        <p className="text-xs text-muted-foreground">
          @Atlas House will be tagged automatically.
        </p>

        {!preview.hasLinkedInToken ? (
          <p className="text-xs text-destructive">
            Your LinkedIn session has expired — please sign out and back in to reconnect, then try again.
          </p>
        ) : null}

        {publish.isError ? (
          <p className="text-xs text-destructive">
            {(publish.error as Error).message}
          </p>
        ) : null}

        <Button
          onClick={() =>
            publish.mutate({
              commentary: commentary ?? '',
              mentionedUserIds: Array.from(selected ?? []),
            })
          }
          disabled={!canPublish || publish.isPending}
        >
          <Linkedin size={14} className="mr-1" />
          {publish.isPending ? 'Posting…' : 'Post to LinkedIn'}
        </Button>
      </CardContent>
    </Card>
  );
}
